'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  Sun,
  Zap,
  AlertCircle,
  MessageSquare,
  ChevronRight,
  ChevronLeft,
  X,
  CheckCircle2,
} from 'lucide-react';

interface WelcomeModalProps {
  onClose: () => void;
}

const STEPS = [
  {
    id: 'today',
    icon: Sun,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-50 dark:bg-amber-900/30',
    title: 'Your Today View',
    description:
      'Today is your home base. At the top you\'ll see an activity digest of what changed since you were last here — new urgents, approvals, schedule updates, and messages.',
    details: [
      'Left column: your schedule for the day',
      'Right column: widgets for urgent items, pending approvals, and staff out',
      'Activity digest auto-dismisses after you\'ve seen it',
    ],
  },
  {
    id: 'quick-actions',
    icon: Zap,
    iconColor: 'text-indigo-500',
    iconBg: 'bg-indigo-50 dark:bg-indigo-900/30',
    title: 'Quick Actions',
    description:
      'The Quick Actions panel on Today lets you handle the most common tasks without leaving the page — approve or deny time-off requests, resolve urgent coverage gaps, and reply to a room.',
    details: [
      'Approve / deny time-off in one click',
      'Resolve urgent gaps directly from Today',
      'Send a quick reply to any room',
    ],
  },
  {
    id: 'needs-attention',
    icon: AlertCircle,
    iconColor: 'text-red-500',
    iconBg: 'bg-red-50 dark:bg-red-900/30',
    title: 'Needs Attention',
    description:
      'Needs Attention surfaces everything that requires action, grouped by category: Coverage Gaps, Time Off Requests, Pending Approvals, and Follow-Ups.',
    details: [
      'Select multiple items with checkboxes for bulk actions',
      '"Assign to me" or "Resolve all" selected items at once',
      'Badge count in the sidebar updates in real time',
    ],
  },
  {
    id: 'rooms',
    icon: MessageSquare,
    iconColor: 'text-teal-500',
    iconBg: 'bg-teal-50 dark:bg-teal-900/30',
    title: 'Using Rooms',
    description:
      'Rooms are team communication channels. Each room has threaded messages, @mentions, file sharing, and optional video calls. Unread counts appear in the sidebar badge.',
    details: [
      'Reply in threads to keep conversations focused',
      '@mention teammates to notify them directly',
      'Start a video call from any room header',
    ],
  },
];

export default function WelcomeModal({ onClose }: WelcomeModalProps) {
  const [step, setStep] = useState(0);
  const [dismissing, setDismissing] = useState(false);
  const { user } = useAuth();
  const supabase = createClient();

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const StepIcon = current.icon;

  async function markSeen() {
    if (user?.id) {
      await supabase
        .from('user_preferences')
        .upsert(
          { user_id: user.id, has_seen_welcome: true },
          { onConflict: 'user_id' }
        );
    }
  }

  function handleClose() {
    setDismissing(true);
    markSeen();
    setTimeout(onClose, 200);
  }

  function handleNext() {
    if (isLast) {
      handleClose();
    } else {
      setStep((s) => s + 1);
    }
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ${
        dismissing ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-modal border border-stone-100 dark:border-slate-700 overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
              Welcome to CFVA Hub
            </span>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-stone-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Dismiss welcome guide"
          >
            <X size={15} />
          </button>
        </div>

        {/* Step content */}
        <div className="px-6 pt-5 pb-6">
          {/* Icon */}
          <div className={`w-12 h-12 rounded-xl ${current.iconBg} flex items-center justify-center mb-4`}>
            <StepIcon size={22} className={current.iconColor} />
          </div>

          {/* Title */}
          <h2 className="text-[18px] font-semibold text-slate-800 dark:text-slate-100 mb-2">
            {current.title}
          </h2>

          {/* Description */}
          <p className="text-[13.5px] text-slate-500 dark:text-slate-400 leading-relaxed mb-4">
            {current.description}
          </p>

          {/* Detail bullets */}
          <ul className="space-y-2">
            {current.details.map((d, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <CheckCircle2
                  size={14}
                  className="shrink-0 mt-0.5 text-slate-300 dark:text-slate-600"
                />
                <span className="text-[13px] text-slate-600 dark:text-slate-400">{d}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex items-center justify-between">
          {/* Step dots */}
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`rounded-full transition-all duration-200 ${
                  i === step
                    ? 'w-5 h-2 bg-indigo-500' :'w-2 h-2 bg-stone-200 dark:bg-slate-700 hover:bg-stone-300 dark:hover:bg-slate-600'
                }`}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="flex items-center gap-1 px-3 py-1.5 text-[13px] font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-stone-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                <ChevronLeft size={14} />
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex items-center gap-1.5 px-4 py-1.5 text-[13px] font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              {isLast ? 'Get started' : 'Next'}
              {!isLast && <ChevronRight size={14} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
